import { useMemo } from 'react';

import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import useAthleteWorkoutsCalendar from 'hooks/workout/useAthleteWorkoutsCalendar';
import type { AthleteEvent, PaginatedAthleteEventList } from 'types';

import type { AthleteCalendarItem } from './types';

/** A month spans up to 42 days; the endpoint's default page of 20 would truncate it. */
const EVENTS_PAGE_LIMIT = '250';

const APPOINTMENT_EVENT_TYPE = 'athlete_appointment';
// Historical data with no event type behind it — not a calendar card.
const IMPORTED_EVENT_TYPE = 'imported_attendance';

interface UseAthleteCalendarParams {
  athleteId?: string;
  startDate?: string;
  endDate?: string;
  showClasses: boolean;
  showAppointments: boolean;
}

interface UseAthleteCalendarResult {
  itemsByDate: Map<string, AthleteCalendarItem[]>;
  isLoading: boolean;
  isError: boolean;
}

function toEventItem(event: AthleteEvent): AthleteCalendarItem {
  return {
    kind: event.type === APPOINTMENT_EVENT_TYPE ? 'appointment' : 'class',
    id: event.athleteEventId,
    name: event.name,
    startTime: event.startTime,
    color: event.color,
    coachName: event.coachName ?? '',
  };
}

/**
 * The day stacks the three views render, merged from two reads: the workouts action
 * returns its own two groups ordered, and this appends the events after them by time.
 */
function useAthleteCalendar({
  athleteId,
  startDate,
  endDate,
  showClasses,
  showAppointments,
}: UseAthleteCalendarParams): UseAthleteCalendarResult {
  const workoutsQuery = useAthleteWorkoutsCalendar({
    athlete: athleteId,
    startDate,
    endDate,
  });

  const eventsQuery = useAthleteEvents({
    athlete: athleteId,
    startDateRange: startDate,
    endDateRange: endDate,
    limit: EVENTS_PAGE_LIMIT,
    hideWaitlist: false,
    enabled: !!athleteId && !!startDate && !!endDate,
  });

  const workoutDays = workoutsQuery.data?.data;
  const eventPages = eventsQuery.data?.pages;

  const itemsByDate = useMemo(() => {
    const byDate = new Map<string, AthleteCalendarItem[]>();

    (workoutDays ?? []).forEach(day => {
      byDate.set(
        day.date,
        day.workouts.map(workout => ({
          kind: 'workout' as const,
          id: workout.workoutId,
          name: workout.name,
          source: workout.source,
          isVisibleToAthletes: workout.isVisibleToAthletes,
          isLogged: workout.isLogged,
          classTypeName: workout.kiloClassTypeName,
          classTypeColor: workout.kiloClassTypeColor,
        })),
      );
    });

    const events = (eventPages ?? [])
      .flatMap((page: { data: PaginatedAthleteEventList }) => page.data.results ?? [])
      .filter((event: AthleteEvent) => event.type !== IMPORTED_EVENT_TYPE)
      .filter((event: AthleteEvent) =>
        event.type === APPOINTMENT_EVENT_TYPE ? showAppointments : showClasses,
      );

    const eventsByDate = new Map<string, AthleteEvent[]>();
    events.forEach((event: AthleteEvent) => {
      eventsByDate.set(event.startDate, [...(eventsByDate.get(event.startDate) ?? []), event]);
    });

    eventsByDate.forEach((dayEvents, date) => {
      const sorted = [...dayEvents].sort((a, b) => a.startTime.localeCompare(b.startTime));
      byDate.set(date, [...(byDate.get(date) ?? []), ...sorted.map(toEventItem)]);
    });

    return byDate;
  }, [workoutDays, eventPages, showClasses, showAppointments]);

  return {
    itemsByDate,
    isLoading: workoutsQuery.isLoading || eventsQuery.isLoading,
    isError: workoutsQuery.isError || eventsQuery.isError,
  };
}

export default useAthleteCalendar;
